<template>
    <ScheduleFollowupDialog
        v-if="!isNadiaChatRefreshEnabled"
        ref="scheduleFollowupDialog"
        :initial-date="initialDate"
        :event-description="inviteDescription"
        :custom-schedule="handleScheduleInDialog" />
    <ChatAgentToolFrame v-if="!isNadiaChatRefreshEnabled" is-actionable :is-complete="action.action_state.submitted">
        <div class="nadia-chat flex flex-col gap-1" data-testid="chat-followup">
            <div class="nadia-chat text-ink">
                {{ action.action_state.submitted ? t("chat.followup.booked", formattedDate) : t("chat.followup.proposed") }}
            </div>
            <div class="text-ink-muted">
                {{ action.action_state.submitted ? t("chat.followup.inviteSent", formattedDate) : t("chat.followup.confirmTime") }}
            </div>
        </div>
        <ScheduleTrigger v-if="!action.action_state.submitted" :disabled="isScheduling" :date="formattedDate" :scheduled="isScheduled" @click="scheduleFollowupDialog.open()" />
        <BaseNotification v-if="hasError" theme="error" hide-dismiss :text="t('chat.followup.errorText')" />
        <BaseButton v-if="!action.action_state.submitted" :disabled="isScheduling" theme="primary" @click="handleSchedule">
            <LoadingSpinner v-if="loading" class="h-4" />{{ t("scheduleFollowup.action") }}
        </BaseButton>
    </ChatAgentToolFrame>
    <ChatAgentToolFrame v-else is-actionable>
        <div v-if="action.action_state.submitted" class="flex flex-col gap-4" data-testid="chat-followup">
            <h3 class="nadia-chat-bold text-center text-ink">{{ t("chat.followup.scheduledTitle") }}</h3>
            <p class="text-ink-muted">{{ t("chat.followup.inviteSent", formattedDate) }}</p>

            <div class="rounded-lgr bg-surface-sunken px-4 py-3 text-ink-muted">{{ dateLabel }}</div>

            <div class="flex items-center gap-3">
                <div class="flex-1 rounded-lgr bg-surface-sunken px-4 py-3 text-ink-muted">{{ startTimeLabel }}</div>
                <span class="text-ink-muted">{{ t("chat.followup.rangeSeparator") }}</span>
                <div class="flex-1 rounded-lgr bg-surface-sunken px-4 py-3 text-ink-muted">{{ endTimeLabel }}</div>
            </div>

            <div v-if="recurring" class="flex items-center gap-2 text-ink-muted">
                <i class="bi bi-arrow-repeat" aria-hidden="true"></i>
                <span>{{ t("chat.followup.recurringLabel") }}</span>
            </div>
        </div>
        <div v-else class="flex flex-col gap-4" data-testid="chat-followup">
            <h3 class="nadia-chat-bold text-center text-ink">{{ t("chat.followup.title") }}</h3>

            <PopoverRoot v-model:open="dateOpen">
                <PopoverTrigger as-child>
                    <button type="button" class="field-trigger" :disabled="isScheduling" :aria-label="t('chat.followup.editDate')">
                        <span>{{ dateLabel }}</span>
                        <i class="bi bi-calendar text-base/none text-ink-muted" aria-hidden="true"></i>
                    </button>
                </PopoverTrigger>
                <PopoverPortal>
                    <PopoverContent side="bottom" align="start" :side-offset="8" class="z-40 rounded-xl border border-popover-border bg-popover-bg p-2 shadow-elevated">
                        <BaseCalendar :model-value="dateValue" :min-value="minValue" :disabled="isScheduling" surface="plain" @update:model-value="onDatePicked" />
                    </PopoverContent>
                </PopoverPortal>
            </PopoverRoot>

            <div class="flex items-center gap-3">
                <div class="min-w-0 flex-1">
                    <BaseSelect v-model="startTime" :options="timeOptions" :disabled="isScheduling" :aria-label="t('chat.followup.startTimeAria')" />
                </div>
                <span class="text-ink-muted">{{ t("chat.followup.rangeSeparator") }}</span>
                <div class="min-w-0 flex-1">
                    <div class="rounded-lgr bg-surface-sunken px-4 py-3 text-ink-muted">{{ endTimeLabel }}</div>
                </div>
            </div>

            <div class="flex items-center justify-between gap-3">
                <span class="text-ink">{{ t("chat.followup.repeatQuestion") }}</span>
                <BaseSwitch v-model="recurring" :disabled="isScheduling" :aria-label="t('chat.followup.repeatAria')" />
            </div>

            <BaseNotification v-if="hasError" theme="error" hide-dismiss :text="t('chat.followup.errorText')" />

            <BaseButton :disabled="isScheduling" class="flex w-full items-center justify-center gap-1" theme="primary" @click="handleSchedule">
                <LoadingSpinner v-if="loading" class="h-4" />{{ t("scheduleFollowup.action") }}
            </BaseButton>
        </div>
    </ChatAgentToolFrame>
</template>

<script setup>
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCalendar from "~vue/components/BaseCalendar.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseSelect from "~vue/components/BaseSelect.vue";
import BaseSwitch from "~vue/components/BaseSwitch.vue";
import ScheduleFollowupDialog from "~vue/components/ScheduleFollowupDialog.vue";
import ScheduleTrigger from "~vue/components/ScheduleTrigger.vue";
import { useDeferredLoading } from "~vue/composables/useDeferredLoading";
import { useNadiaChatRefreshEnabled } from "~vue/composables/useNadiaChatRefresh";
import { useScheduleFollowup } from "~vue/composables/useScheduleFollowup.js";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { formatDate } from "~vue/utils/formatDate";
import { logError } from "~vue/utils/logUtils";
import { DateTime } from "luxon";
import { PopoverContent, PopoverPortal, PopoverRoot, PopoverTrigger } from "reka-ui";
import { computed, inject, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

import { convertDateTimeToDateValue } from "../../dateUtils";
import ChatAgentToolFrame from "./ChatAgentToolFrame.vue";

const { t } = useI18n();
const isScheduling = ref(false);
const { loading } = useDeferredLoading({ loadingState: isScheduling });
const isNadiaChatRefreshEnabled = useNadiaChatRefreshEnabled();
const scheduleFollowupDialog = useTemplateRef("scheduleFollowupDialog");

const props = defineProps({
    action: {
        type: Object,
        required: true,
    },
    lineIdx: { type: Number, required: true },
    messageId: { type: Number, required: true },
});

const { initialDate, formattedDate, scheduleFollowup, isScheduled, inviteDescription } = useScheduleFollowup({
    line: props.action,
    messageId: props.messageId,
    lineIdx: props.lineIdx,
    source: "chat_agent_tool",
});

const selectedDate = ref(new Date(initialDate.value));
const userDetails = inject("user_details");
const recurring = ref(props.action.action_state.submitted ? props.action.action_params.recurring : userDetails.value.prefers_weekly_check_in);
const dateOpen = ref(false);
const hasError = ref(false);

const luxonSelected = () => DateTime.fromJSDate(selectedDate.value);

const dateLabel = computed(() => formatDate(luxonSelected(), "date-long"));
const startTimeLabel = computed(() => formatDate(luxonSelected(), "time"));
const endTimeLabel = computed(() => formatDate(luxonSelected().plus({ minutes: 15 }), "time"));

const dateValue = computed(() => convertDateTimeToDateValue(luxonSelected()));
const minValue = computed(() => convertDateTimeToDateValue(DateTime.now()));

const startTime = computed({
    get: () => luxonSelected().toFormat("HH:mm"),
    set: (value) => {
        const [hour, minute] = String(value).split(":").map(Number);
        selectedDate.value = luxonSelected().set({ hour, minute, second: 0, millisecond: 0 }).toJSDate();
    },
});

const timeOptions = computed(() => {
    const base = DateTime.fromObject({ hour: 0, minute: 0 });
    const times = new Set(Array.from({ length: 96 }, (_, index) => base.plus({ minutes: 15 * index }).toFormat("HH:mm")));
    times.add(startTime.value);
    return [...times].sort().map((key) => ({ key, label: formatDate(DateTime.fromFormat(key, "HH:mm"), "time") }));
});

function onDatePicked(value) {
    if (!value) {
        return;
    }
    const current = luxonSelected();
    selectedDate.value = DateTime.fromObject({
        year: value.year,
        month: value.month,
        day: value.day,
        hour: current.hour,
        minute: current.minute,
    }).toJSDate();
    dateOpen.value = false;
}

async function handleSchedule() {
    if (isScheduling.value) {
        return;
    }
    isScheduling.value = true;
    dateOpen.value = false;
    hasError.value = false;

    try {
        await scheduleFollowup({
            recurring: isNadiaChatRefreshEnabled.value ? recurring.value : scheduleFollowupDialog.value.recurring,
            selectedDate: selectedDate.value,
            sync: true,
        });
    } catch (e) {
        logError(e);
        hasError.value = true;
    } finally {
        isScheduling.value = false;
    }
}

async function handleScheduleInDialog({ recurring, selectedDate: scheduledDate }) {
    hasError.value = false;
    const result = await scheduleFollowup({ recurring, selectedDate: scheduledDate, sync: true });
    selectedDate.value = scheduledDate;
    return result;
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .field-trigger {
        @apply flex w-full items-center justify-between gap-2 rounded-lg border border-control-border bg-control-bg px-4 py-3 text-left text-control-text transition-colors hover:border-control-border-strong focus-field;
    }
}
</style>
